import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * Route-level code splitting (QA 2026-09-25 #14).
 *
 * The booking funnel a customer walks through (location, services, add-ons,
 * schedule, confirmation) stays in the main bundle. Everything a customer
 * never reaches - the fleet dashboard, the franchise manager / tech / shop
 * screens with their charts, scanners and AWS SDK, the account pages, the
 * auth pages and the Stripe payment step - is its own chunk, fetched only
 * when its route renders.
 *
 * A split bundle has one failure mode a single bundle does not: a deploy
 * replaces the hashed chunk files, so a tab opened before it asks for a chunk
 * that no longer exists. Every route here is behind a full page load, and the
 * booking state is persisted (app/localStorage), so the fix is to reload once
 * - which fetches the new index.html and its chunks. The timestamp guard
 * stops a chunk that is genuinely unreachable (offline) from reloading in a
 * loop: the second failure within the window is thrown to the caller.
 */

export const CHUNK_RELOAD_KEY = 'chunkReloadAt';
const RELOAD_WINDOW_MS = 30000;

function reloadedRecently(): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    return Date.now() - at < RELOAD_WINDOW_MS;
  } catch (e) {
    // No sessionStorage: never reload, so there is nothing to loop on.
    return true;
  }
}

export function importWithReload<T>(factory: () => Promise<T>): Promise<T> {
  return factory().catch((error) => {
    if (reloadedRecently()) {
      throw error;
    }
    try {
      window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch (e) {
      // guarded above
    }
    window.location.reload();
    // Never settles: the page is going away.
    return new Promise<T>(() => undefined);
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => importWithReload(factory));
}
